'use client';

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearch } from '@/context/listing-search-provider';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '@/utils/local-storage/safe-local-storage';
import { Listing } from '@/utils/platform-api/__generated__/graphql';
import { ClientBrandConfig } from 'types/brand-config';

import { useBreakpoint } from '../../utils/useBreakpoint';
import { LOCATION_POLYGON_PARAM } from './constance';

type MapContextType = {
  currentListing: Listing | null;
  setCurrentListing: (listing: Listing | null) => void;
  hoveredListing: Listing | null;
  setHoveredListing: (listing: Listing | null) => void;
  showMap: boolean;
  setShowMap: (showMap: boolean) => void;
  showListingDialog: boolean;
  setShowListingDialog: (showMap: boolean) => void;
};

const defaultState: MapContextType = {
  currentListing: null,
  setCurrentListing() {},
  hoveredListing: null,
  setHoveredListing() {},
  showMap: false,
  setShowMap() {},
  showListingDialog: false,
  setShowListingDialog() {},
};

export const MapContext = createContext<MapContextType>(defaultState);

const MapProvider: React.FC<
  PropsWithChildren<{
    brand: ClientBrandConfig;
    disableMap?: boolean;
    /**
     * Fired when the map subsystem first becomes "active" — i.e. the user is
     * about to see pins on screen. This happens when `localStorage.showMap` /
     * brand default resolves to `true`, when the user opens the map for the
     * first time, or when `location_polygon` is already in the URL on mount.
     *
     * Idempotent — the parent owns dedupe (a ref guards against duplicate
     * fetches when init effects fire on top of a polygon-driven mount).
     */
    onMapPinsRequested?: () => void;
  }>
> = ({ children, brand, disableMap, onMapPinsRequested }) => {
  const { searchParams, navigate } = useSearch();
  const [currentListing, setCurrentListing] = useState<Listing | null>(null);
  const [hoveredListing, setHoveredListing] = useState<Listing | null>(null);
  const [showListingDialog, setShowListingDialog] = useState(false);
  const breakpoint = useBreakpoint();
  const useDesktopContent =
    breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === '2xl';
  const [showMap, setShowMap] = useState(false);
  /** Only toggle `.map-hidden-element` nodes inside listing search UI — not `document` (site Header also uses that class). */
  const mapChromeScopeRef = useRef<HTMLDivElement>(null);

  // Keep the latest callback in a ref so we can fire it from effects without
  // re-running them when the parent's identity-unstable callback changes.
  const onMapPinsRequestedRef = useRef(onMapPinsRequested);
  useEffect(() => {
    onMapPinsRequestedRef.current = onMapPinsRequested;
  }, [onMapPinsRequested]);
  const requestMapPins = useCallback(() => {
    if (disableMap) return;
    onMapPinsRequestedRef.current?.();
  }, [disableMap]);

  // searchParams change frequently; keep a ref so the init effect can read the
  // current polygon flag without re-running on every URL update.
  const searchParamsRef = useRef(searchParams);
  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  const handleShowMap = useCallback((showMap: boolean) => {
    setShowMap(showMap);
    setLocalStorageItem('showMap', showMap.toString());
    if (showMap) {
      requestMapPins();
    }
    if (!showMap && searchParams.has('location_polygon')) {
      const pathname = window.location.pathname;
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.delete('location_polygon');
      newParams.delete('zoom');
      if (newParams.size) {
        navigate(pathname + '?' + newParams.toString(), { scroll: false });
      } else {
        navigate(pathname, { scroll: false });
      }
    }
  }, [searchParams, navigate, requestMapPins]);
  useEffect(() => {
    if (disableMap) {
      return;
    }
    const root = mapChromeScopeRef.current;
    if (!root) {
      return;
    }
    const defaultHeaders = root.querySelectorAll<HTMLElement>(
      '.map-hidden-element'
    );
    const defaultFooter = document.getElementById('default-footer');
    if (!useDesktopContent) {
      // Hide listing-search chrome inside this provider when the map is shown on mobile
      if (defaultHeaders.length) {
        defaultHeaders.forEach((header) => {
          header.style.display = showMap ? 'none' : '';
        });
      }
      if (defaultFooter) {
        defaultFooter.style.display = showMap ? 'none' : '';
      }
    } else {
      if (defaultHeaders.length) {
        defaultHeaders.forEach((header) => {
          header.style.display = showMap ? 'none' : '';
        });
      }
      if (defaultFooter) {
        defaultFooter.style.display = showMap ? 'none' : '';
      }
    }
  }, [showMap, useDesktopContent, disableMap]);

  const handleSetCurrentListing = useCallback((listing: Listing | null) => {
    setCurrentListing(listing);
  }, []);
  const handleSetHoveredListing = useCallback((listing: Listing | null) => {
    setHoveredListing(listing);
  }, []);
  const providerValue = useMemo(
    () => ({
      currentListing,
      setCurrentListing: handleSetCurrentListing,
      hoveredListing,
      setHoveredListing: handleSetHoveredListing,
      showMap,
      setShowMap: handleShowMap,
      showListingDialog,
      setShowListingDialog,
    }),
    [
      currentListing,
      handleSetCurrentListing,
      hoveredListing,
      handleSetHoveredListing,
      handleShowMap,
      showMap,
      showListingDialog,
      setShowListingDialog,
    ]
  );

  useEffect(() => {
    if (disableMap) {
      return;
    }
    let resolved = false;
    if (useDesktopContent && brand.showMap) {
      setShowMap(true);
      resolved = true;
    }
    const stored = getLocalStorageItem('showMap');
    if (stored) {
      const fromStorage = stored === 'true';
      setShowMap(fromStorage);
      resolved = fromStorage;
    }
    if (resolved || searchParamsRef.current.has(LOCATION_POLYGON_PARAM)) {
      requestMapPins();
    }
  }, [brand.showMap, useDesktopContent, disableMap, requestMapPins]);

  return (
    <MapContext.Provider value={providerValue}>
      <div ref={mapChromeScopeRef} className="contents">
        {children}
      </div>
    </MapContext.Provider>
  );
};

export default MapProvider;
